// App.js
import { useEffect, useState, useMemo, useCallback, useRef, memo } from "react";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DialogContent, FormControl } from "@mui/material";
import CustomTextField from '../../../main/widgets/CustomTextField';
import { Alert, DialogActions, DialogTitle, Divider, FormLabel, Modal, ModalDialog, Stack, Tooltip, Typography, Card, Chip } from "@mui/joy";
import Grid from '@mui/joy/Grid';
import Button from '@mui/joy/Button';
import { useNavigate } from 'react-router-dom'
import CustomerOptionList from '../../../main/widgets/CustomersOptionList';
import Snackbar from '@mui/joy/Snackbar';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import { postInvoice, putInvoice, readInvoice, readShipments } from '../../../main/API/invoices';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { red } from '@mui/material/colors';
import LinearProgress from '@mui/joy/LinearProgress';
import { regenerateInvoicesRequest } from "../../../main/API/invoices";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { DataGrid } from '@mui/x-data-grid';
import { getPaymentLinkExpirationStatus } from '../../../main/services/format';
import { getShipmentFormPaymentColumns } from '../../../main/widgets/shipmentPaymentDataGridColumns';
import { getPaymentStatusIconConfig, formatPaymentStatus } from '../../../main/widgets/PaymentStatusOptionList';
import { OmrAmount } from '../../../main/components/OmrAmount';
import { shipmentTotal } from '../../../shipments_manager/views/htmlDocumentUtils';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import {
  isZeroOrMissingShipmentPrice,
  parseInvoiceShipmentsFieldError,
} from '../../../main/utils/invoiceGenerationResponse';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';
import { formatInvoiceQty } from '../../../main/utils/formatInvoiceQty';
import { isRtlLocale } from '../../../../i18n/language';
import InvoicePaymentLinksSection from './InvoicePaymentLinksSection';
import CopyPaymentLinkButton from './CopyPaymentLinkButton';
import { buildPublicInvoicePayUrl } from '../../../main/utils/publicInvoicePayUrl';
import { sendWhatsAppInvoiceNotification } from '../../../main/API/whatsapp';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

/** Fixed height + no autoHeight keeps row virtualization efficient (autoHeight can be costly). */
const SHIPMENT_GRID_HEIGHT = 380;
const SHIPMENT_ROW_HEIGHT = 52;

const InvoiceShipmentsDataGrid = memo(function InvoiceShipmentsDataGrid({
  rows,
  columns,
  noRowsLabel,
}) {
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(r) => r.id}
      hideFooter
      disableRowSelectionOnClick
      disableColumnMenu
      rowHeight={SHIPMENT_ROW_HEIGHT}
      localeText={{ noRowsLabel }}
      sx={{
        height: SHIPMENT_GRID_HEIGHT,
        border: 'none',
        '& .MuiDataGrid-cell': { py: 0.5, alignItems: 'center' },
        '& .MuiDataGrid-columnHeaders': { maxHeight: 44, minHeight: 44 },
      }}
    />
  );
});

export default function InvoiceForm({ formID }) {
  const { t, i18n } = useTranslation('ui');
  const qtyLang = isRtlLocale(i18n.language) ? 'ar' : 'en';
  const { hasPermission } = usePermissions();

  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);



  //control values

  //Snackbar controller
  const [successUpdate, setSuccessUpdate] = useState(false);
  const [failedUpdate, setFailedUpdate] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  //Form Values
  const [id, setId] = useState("");
  const [shipmentOwner, setShipmentOwner] = useState(null);
  const [note, setNote] = useState("");
  const [invoice, setInvoice] = useState(null);


  //invoice for edit and view form
  const [userShipments, setUserShipments] = useState([]);
  const [allUserShipments, setAllUserShipments] = useState([]);
  const [selectedUserShipments, setSelectedUserShipments] = useState([]);
  const [selectAllUserShipments, setSelectAllUserShipments] = useState(false);

  //Regenerate Invoice
  const [isRegeneratingInvoicesLoading, setRegeneratingInvoicesLoading] = useState(false);
  const [openRegenerateInvoicesAlert, setOpenRegenerateInvoicesAlert] = useState(false);
  const [sendInvoicesMessages, setSendInvoicesMessages] = useState(true);
  const [invoicesResponseMessages, setInvoicesResponseMessages] = useState("");
  const [successGenerateInvoices, setSuccessGenerateInvoices] = useState(false);
  const [failedGenerateInvoices, setFailedGenerateInvoices] = useState(false);
  const [createdInvoicesCount, setCreatedInvoicesCount] = useState(0);

  const [loadingMsg, setLoadingMsg] = useState(false);
  const [successSentMsg, setSuccessSentMsg] = useState(false);
  const [failedSentMsg, setFailedSentMsg] = useState(false);

  const [shipmentsError, setShipmentsError] = useState(false);
  const [shipmentsValidationMessage, setShipmentsValidationMessage] = useState('');

  const getShipmentIdsForSave = useCallback(() => {
    if (formID == null) {
      return selectedUserShipments.map((value) => value.id);
    }
    const locked =
      invoice &&
      (invoice.payment_status === 'paid' || invoice.payment_status === 'overpaid');
    let ids = selectedUserShipments.map((value) => value.id);
    if (locked && invoice.shipments != null && invoice.shipments.length > 0) {
      ids = invoice.shipments.map((s) => (s && typeof s === 'object' ? s.id : s));
    }
    return ids;
  }, [formID, invoice, selectedUserShipments]);

  useEffect(() => {
    if (selectedUserShipments.length > 0) {
      setShipmentsError(false);
      setShipmentsValidationMessage('');
    }
  }, [selectedUserShipments]);

  //submit form 
  const handleSubmit = async (event) => {
    event.preventDefault();

    const requiredPerm = formID == null
      ? PERMISSIONS.INVOICES_CREATE
      : PERMISSIONS.INVOICES_UPDATE;
    if (!hasPermission(requiredPerm)) return;

    setShipmentsError(false);
    setShipmentsValidationMessage('');

    const shipmentIdsForSave = getShipmentIdsForSave();
    if (shipmentIdsForSave.length === 0) {
      setShipmentsError(true);
      return;
    }

    const zeroPriceSelected = selectedUserShipments.filter(
      (shipment) => isZeroOrMissingShipmentPrice(shipment.price)
    );
    if (zeroPriceSelected.length > 0) {
      setShipmentsError(true);
      setShipmentsValidationMessage(t('invoiceGeneration.zeroPricePreCheck'));
      return;
    }

    setIsSubmitLoading(true);

    //add new invoice 
    if (formID == null) {


      try {
        console.log(
        );
        const response = await postInvoice({
          customer: shipmentOwner.id,
          note: note,
          shipments: shipmentIdsForSave
        });

        if (response.status != 201) {
          setFailedUpdate(true);
          setErrorMessage(response.statusText);
        }
        else {
          window.location.href = "/Invoices/" + response.data.id + "/EditInvoice";
        }

      }
      catch (e) {
        const shipmentsFieldError = parseInvoiceShipmentsFieldError(e, t);
        if (shipmentsFieldError) {
          setShipmentsError(true);
          setShipmentsValidationMessage(shipmentsFieldError);
        } else {
          setFailedUpdate(true);
          setErrorMessage(translateAxiosErrorMessage(e, t) || t('invoiceGeneration.failed'));
        }
        setIsSubmitLoading(false);
      }

    }


    //update existing invoice
    else {
      setOpenRegenerateInvoicesAlert(true);
      try {
        const response = await putInvoice({
          id: formID,
          note: note,
          customer: shipmentOwner.id,
          shipments: shipmentIdsForSave,
        });

        //update is OK
        if (response.status == 200) {

          setInvoice(response.data);

          setSuccessUpdate(true);
        }

        //Update NOT OK
        else {
          setFailedUpdate(true);
          setErrorMessage(response.statusText);
        }

      }
      catch (e) {
        const shipmentsFieldError = parseInvoiceShipmentsFieldError(e, t);
        if (shipmentsFieldError) {
          setShipmentsError(true);
          setShipmentsValidationMessage(shipmentsFieldError);
        } else {
          setFailedUpdate(true);
          setErrorMessage(translateAxiosErrorMessage(e, t) || t('invoiceGeneration.failed'));
        }
        setIsSubmitLoading(false);
      }
    }
    setIsSubmitLoading(false);
  }

  function FillAllVariables(invoice) {
    setId(invoice.id);
    setShipmentOwner(invoice.customer);
    setNote(invoice.note);
    //setUserShipments(userShipments.concat(invoice.shipments));
    //set the fetched shipments to checked  
    setSelectedUserShipments(invoice.shipments);

  }





  //This function to control getting user shipments from API function 
  const getUserShipments = async (user) => {
    setLoading(true);
    //Reset selected shipments for create invoice only
    console.log(formID);
    if (formID == undefined) {
      setSelectedUserShipments([]);
      setSelectAllUserShipments(false);
    }



    //get all not invoiced shipments for specific user
    await readShipments(user)
      .then((response) => response.data)
      .then((actualData) => {
        setUserShipments(actualData);
      })
      .catch((err) => {
        console.log(err.message);

      });
  };




  //This function to control getting user shipments from API function onChange the user 
  const getUserShipmentsOnChange = async (user) => {
    setLoading(true);
    //Reset selected shipments for create invoice only
    console.log(formID);
    if (formID == undefined) {
      setSelectedUserShipments([]);
      setSelectAllUserShipments(false);
    }



    //get all not invoiced shipments for specific user
    await readShipments(user)
      .then((response) => response.data)
      .then((actualData) => {
        setUserShipments(actualData);
      })
      .catch((err) => {
        console.log(err.message);

      });
    setLoading(false);
  };





  //This function to control getting user shipments from API function and not selected in the invoice 
  const getNotSelectedShipments = async (user) => {

    //get all shipments for specific user
    await readShipments(user)
      .then((response) => response.data)
      .then((actualData) => {
        setAllUserShipments(actualData);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err.message);
      });
  };

  const getInvoice = async (InvoiceID) => {
    if (!hasPermission(PERMISSIONS.INVOICES_VIEW)) return;
    try {
      setLoading(true);
      const response = await readInvoice(InvoiceID);
      const actualData = response.data;
      setInvoice(actualData);
      await getNotSelectedShipments(actualData.customer);
      setUserShipments(actualData.shipments || []);

      FillAllVariables(actualData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    }
  };

  /** Refresh checkout / payment fields without toggling page loading (avoids scroll jump). */
  const refreshInvoiceQuietly = useCallback(async () => {
    if (formID == null || !hasPermission(PERMISSIONS.INVOICES_VIEW)) return;
    try {
      const response = await readInvoice(formID);
      setInvoice(response.data);
    } catch (err) {
      console.log(err.message);
    }
  }, [formID, hasPermission]);




  //table paid icon
  function paymentStatus(payment_link_expire_at, payment_link, paid) {
    if (paid) {
      return (
        <Box>
          <Tooltip title={t('invoiceForm.paidLabel')}>
            <span> {/* Ensures the child element is always valid */}
              <MonetizationOnIcon sx={{ color: "green" }} />
            </span>
          </Tooltip>
          {t('invoiceForm.paidLabel')}
        </Box>
      );
    }

    if (!payment_link && !payment_link_expire_at) {
      return (<Box>{t('invoiceForm.paymentLinkNotCreatedShort')} <Tooltip title={t('invoiceManager.ttPaymentLinkNotCreated')}><MonetizationOnIcon sx={{ color: "orange" }} /></Tooltip></Box>);
    }
    if (!payment_link && payment_link_expire_at) {
      return (<Box><Tooltip title={t('invoiceManager.ttPaymentCanceled')}><MonetizationOnIcon sx={{ color: red[500] }} /></Tooltip> {t('invoiceForm.paymentCanceledShort')}</Box>);
    }

    const expirationStatus = getPaymentLinkExpirationStatus(payment_link_expire_at, payment_link);
    
    if (expirationStatus.status === "unlimited") {
      return (<Box><Tooltip title={t('invoiceForm.unlimitedPaymentLink')}><MonetizationOnIcon sx={{ color: red[500] }} /></Tooltip> {t('invoiceForm.unlimitedPaymentLink')}</Box>);
    } else if (expirationStatus.isExpired) {
      return (<Box><Tooltip title={t('invoiceForm.paymentLinkExpiredShort')}><MonetizationOnIcon sx={{ color: red[100] }} /></Tooltip> {t('invoiceForm.paymentLinkExpiredShort')}</Box>);
    } else {
      return (<Box><Tooltip title={t('invoiceForm.waitingForPaymentShort')} ><MonetizationOnIcon sx={{ color: "gray" }} /></Tooltip> {t('invoiceForm.waitingForPaymentShort')} </Box>);
    }

  }

  function paidIcon(paid) {
    if (paid) {
      return (<MonetizationOnIcon color='success'></MonetizationOnIcon>);
    }
    else {
      return (<MonetizationOnIcon sx={{ color: red[500] }}></MonetizationOnIcon>);
    }

  }

  const isInvoicePaidLocked = useMemo(() => {
    if (formID == null || !invoice) return false;
    const ps = invoice.payment_status;
    return ps === 'paid' || ps === 'overpaid';
  }, [formID, invoice]);

  const controlCheckbox = useCallback(
    (checked, shipment) => {
      if (isInvoicePaidLocked) return;
      if (checked) {
        setSelectedUserShipments((prevList) => {
          if (prevList.some((v) => v.id === shipment.id)) return prevList;
          return [...prevList, shipment];
        });
      } else {
        setSelectedUserShipments((prevList) => prevList.filter((value) => value.id !== shipment.id));
      }
    },
    [isInvoicePaidLocked]
  );

  const selectedIds = useMemo(
    () => new Set(selectedUserShipments.map((s) => s.id)),
    [selectedUserShipments]
  );

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const allRowsDeduped = useMemo(() => {
    const m = new Map();
    [...userShipments, ...allUserShipments].forEach((r) => {
      if (r && r.id != null && !m.has(r.id)) m.set(r.id, r);
    });
    return Array.from(m.values());
  }, [userShipments, allUserShipments]);

  const [includedRows, availableRows] = useMemo(() => {
    const inc = [];
    const av = [];
    for (let i = 0; i < allRowsDeduped.length; i += 1) {
      const r = allRowsDeduped[i];
      if (selectedIds.has(r.id)) inc.push(r);
      else av.push(r);
    }
    return [inc, av];
  }, [allRowsDeduped, selectedIds]);

  const invoiceTotalPrice = useMemo(
    () => shipmentTotal(selectedUserShipments),
    [selectedUserShipments]
  );

  const paymentStatusIcon = useCallback((paymentStatus) => {
    const config = getPaymentStatusIconConfig(paymentStatus);
    return (
      <Tooltip title={config.title}>
        <MonetizationOnIcon
          color={config.color === 'success' ? 'success' : undefined}
          sx={config.color !== 'success' ? { color: config.color } : undefined}
        />
      </Tooltip>
    );
  }, []);

  const shipmentColumns = useMemo(
    () => [
      {
        field: 'selection',
        headerName: '',
        width: 52,
        flex: 0.4,
        sortable: false,
        renderCell: (params) => (
          <NativeCheckbox
            onChange={(event) => controlCheckbox(event.target.checked, params.row)}
            checked={selectedIdsRef.current.has(params.row.id)}
            disabled={isInvoicePaidLocked}
          />
        ),
      },
      {
        field: 'id',
        headerName: t('trackingEvent.colId'),
        minWidth: 100,
        flex: 1,
        renderCell: (params) => (
          <Box onClick={() => window.open(`../../Shipment/${params.row.id}/EditShipment`, '_blank')} m={1}>
            {params.row.id}
          </Box>
        ),
      },
      {
        field: 'shipper_reference',
        headerName: t('invoiceForm.colShipperReference'),
        minWidth: 150,
        flex: 2,
      },
      {
        field: 'tracking_number',
        headerName: t('invoiceForm.colTrackingNumber'),
        minWidth: 150,
        flex: 2,
      },
      {
        field: 'shipment_unit_serial_number',
        headerName: t('invoiceForm.colUnitSerial'),
        minWidth: 140,
        flex: 1.5,
        renderCell: (params) =>
          params.row.shipment_unit_serial_number != null && params.row.shipment_unit_serial_number !== ''
            ? params.row.shipment_unit_serial_number
            : '—',
      },
      {
        field: 'invoice_quantity',
        headerName: t('invoiceForm.colInvoiceQuantity'),
        minWidth: 160,
        flex: 1.5,
        renderCell: (params) => {
          const label = formatInvoiceQty(params.row, qtyLang);
          const metric = params.row.billing_metric;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
              <Typography level="body-sm">{label || '—'}</Typography>
              {metric ? (
                <Chip size="sm" variant="soft" color="neutral">
                  {metric}
                </Chip>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'price',
        headerName: t('invoiceForm.colPrice'),
        minWidth: 100,
        flex: 1,
        renderCell: (params) => (params.row.price != null ? <OmrAmount value={params.row.price} /> : null),
      },
      {
        field: 'payment_status',
        headerName: t('widgets.paymentStatus'),
        minWidth: 120,
        flex: 1.5,
        renderCell: (params) => paymentStatusIcon(params.row.payment_status),
      },
      {
        field: 'current_location',
        headerName: t('invoiceForm.colCurrentLocation'),
        minWidth: 150,
        flex: 2,
      },
      {
        field: 'status',
        headerName: t('invoiceForm.status'),
        minWidth: 120,
        flex: 1.5,
      },
    ],
    [t, qtyLang, controlCheckbox, paymentStatusIcon, isInvoicePaidLocked]
  );

  //generate invoices function 
  const regenerateInvoices = async () => {
    if (!hasPermission(PERMISSIONS.INVOICES_REGENERATE)) return;

    setRegeneratingInvoicesLoading(true);
    await regenerateInvoicesRequest([id], sendInvoicesMessages)
      .then(async actualData => {
        //control success
        if (actualData.status == 200 && actualData.data.created_invoice_count != 0) {
          setInvoicesResponseMessages(actualData.data.message);
          //send notification
          setSuccessGenerateInvoices(true);


        }
        else if (actualData.data.created_invoice_count == 0) {
          setInvoicesResponseMessages(t('invoiceForm.regenerateError'));
          //send notification
          setFailedGenerateInvoices(true);
        }
        //control other responses
        else {
          setInvoicesResponseMessages(t('invoiceForm.regenerateErrorGeneric'));
          //send notification
          setFailedGenerateInvoices(true);
        }
        setCreatedInvoicesCount(actualData.data.regenerated_invoice_count);

        //stop loading
        setRegeneratingInvoicesLoading(false);

        setOpenRegenerateInvoicesAlert(false);
      }).catch((err) => {

        //stop loading 
        setRegeneratingInvoicesLoading(false);
        console.log(err.message);
        setOpenRegenerateInvoicesAlert(false);
      });

  }

  const sendWhatsAppMessage = async (invoiceID) => {
    if (!hasPermission(PERMISSIONS.WHATSAPP_SEND_INVOICE_NOTIFICATION)) return;
    try {
      setLoadingMsg(true);
      const response = await sendWhatsAppInvoiceNotification(invoiceID);
      if (response.status === 200) {
        setSuccessSentMsg(true);
      } else {
        setFailedSentMsg(true);
      }
    } catch (e) {
      setFailedSentMsg(true);
    } finally {
      setLoadingMsg(false);
    }
  };

  useEffect(() => {
    if (formID != null) {
      getInvoice(formID);
    }
  }, [formID]);

  const whatsappPayUrl = invoice ? buildPublicInvoicePayUrl(invoice.payment_access_token) : null;

  return (
    <Box marginTop={7} sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      <Box>
        <form onSubmit={handleSubmit}>
          <FormControl sx={{ width: '100%' }}>
            <Typography marginBottom={3}>
              <Typography level="h1">{formID != null ? t('invoiceForm.titleUpdate') : t('invoiceForm.titleAdd')}</Typography>
            </Typography>

            <Grid container spacing={2}>
              {/* Basic Info Section */}
              {formID != null && (
                <Grid xs={12} md={6}>
                  <CustomTextField
                    disabled={true}
                    type="text"
                    onChange={(event) => setId(event.target.value)}
                    value={id}
                    label={t('invoiceForm.labelId')}
                    fullWidth
                  />
                </Grid>
              )}

              <Grid xs={12}>
                <CustomerOptionList
                  disabled={formID != null || isLoading}
                  defaultValue={shipmentOwner}
                  required
                  onChange={(event, value) => {
                    setShipmentOwner(value);
                    getUserShipmentsOnChange(value);
                  }}
                />
              </Grid>

              <Grid xs={12}>
                <CustomTextField
                  type="text"
                  label={t('invoiceForm.labelNote')}
                  onChange={(event) => setNote(event.target.value)}
                  value={note}
                  fullWidth
                />
              </Grid>

              {selectedUserShipments.length > 0 && (
                <Grid xs={12} md={6}>
                  <FormLabel>{t('invoiceForm.labelInvoiceTotal')}</FormLabel>
                  <Typography level="title-lg">
                    <OmrAmount value={invoiceTotalPrice} />
                  </Typography>
                </Grid>
              )}

              {formID != null && (
                <>
                  <Grid xs={12} md={6}>
                    <FormLabel>{t('invoiceForm.paymentStatus')}</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {invoice && paymentStatusIcon(invoice.payment_status)}
                      {invoice && (
                        <Typography>
                          {invoice && invoice.payment_status ? formatPaymentStatus(invoice.payment_status) : t('invoiceForm.unpaid')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid xs={12} md={3}>
                    <FormLabel>{t('invoiceForm.amountPaid')}</FormLabel>
                    <Typography level="title-md">
                      {invoice && invoice.amount_paid != null
                        ? <OmrAmount value={invoice.amount_paid} />
                        : '—'}
                    </Typography>
                  </Grid>

                  <Grid xs={12} md={3}>
                    <FormLabel>{t('invoiceForm.amountDue')}</FormLabel>
                    <Typography level="title-md">
                      {invoice && invoice.amount_due != null
                        ? <OmrAmount value={invoice.amount_due} />
                        : '—'}
                    </Typography>
                  </Grid>

                  <Grid xs={12} md={6}>
                    <FormLabel>{t('invoiceForm.whatsappPaymentLink')}</FormLabel>
                    <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
                      {t('invoiceForm.whatsappPaymentLinkHint')}
                    </Typography>
                    {whatsappPayUrl ? (
                      <CopyPaymentLinkButton
                        paymentLink={whatsappPayUrl}
                        tooltip={t('invoiceForm.copyPaymentLink')}
                      />
                    ) : (
                      <Box>
                        <Typography>{t('invoiceForm.noPaymentLink')}</Typography>
                      </Box>
                    )}
                  </Grid>
                </>
              )}
            </Grid>

            <Box marginTop={4}>
              {isLoading ? (
                <Box m={5} display="flex" justifyContent="center">
                  <LinearProgress variant="solid" />
                </Box>
              ) : (
                <Box>
                  <Typography level="h2" sx={{ mb: 0.5 }}>{t('invoiceForm.sectionCustomerShipments')}</Typography>
                  <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600', maxWidth: 900 }}>
                    {t('invoiceForm.sectionShipmentsIntro')}
                  </Typography>
                  {shipmentsError && (
                    <Alert color="danger" variant="soft" role="alert" sx={{ mb: 2, maxWidth: 900 }}>
                      {shipmentsValidationMessage || t('invoiceForm.shipmentsRequired')}
                    </Alert>
                  )}
                  {formID != null && isInvoicePaidLocked && (
                    <Alert color="warning" variant="soft" sx={{ mb: 2, maxWidth: 900 }}>
                      {t('invoiceForm.shipmentsLockedPaid')}
                    </Alert>
                  )}
                  <Stack spacing={2.5}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: '12px',
                        borderLeft: '4px solid',
                        borderLeftColor: 'success.500',
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography level="title-md">{t('invoiceForm.sectionOnInvoice')}</Typography>
                        <Chip size="sm" variant="soft" color="success">
                          {includedRows.length}
                        </Chip>
                        {invoiceTotalPrice > 0 && (
                          <Chip size="sm" variant="soft" color="primary">
                            {t('invoiceForm.labelInvoiceTotal')}: <OmrAmount value={invoiceTotalPrice} />
                          </Chip>
                        )}
                      </Box>
                      <InvoiceShipmentsDataGrid
                        rows={includedRows}
                        columns={shipmentColumns}
                        noRowsLabel={t('invoiceForm.emptyOnInvoice')}
                      />
                    </Card>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: '12px',
                        borderLeft: '4px solid',
                        borderLeftColor: 'neutral.400',
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography level="title-md">{t('invoiceForm.sectionNotOnInvoice')}</Typography>
                        <Chip size="sm" variant="soft" color="neutral">
                          {availableRows.length}
                        </Chip>
                      </Box>
                      <InvoiceShipmentsDataGrid
                        rows={availableRows}
                        columns={shipmentColumns}
                        noRowsLabel={t('invoiceForm.emptyNotOnInvoice')}
                      />
                    </Card>
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Actions Section */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid xs={12} md={8}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <PermissionGate
                    permission={formID != null ? PERMISSIONS.INVOICES_UPDATE : PERMISSIONS.INVOICES_CREATE}
                  >
                    <Button loading={isSubmitLoading} type="submit">
                      {formID != null ? t('invoiceForm.update') : t('invoiceForm.submit')}
                    </Button>
                  </PermissionGate>
                  {formID != null && (
                    <PermissionGate permission={PERMISSIONS.WHATSAPP_SEND_INVOICE_NOTIFICATION}>
                      <Stack spacing={1} sx={{ width: '100%' }}>
                        {invoice && !invoice.payment_link ? (
                          <Alert color="warning" variant="soft" sx={{ maxWidth: 480 }}>
                            {t('invoiceForm.whatsappSendNoLinkWarning')}
                          </Alert>
                        ) : null}
                        <Button
                          loading={loadingMsg}
                          onClick={() => sendWhatsAppMessage(id)}
                          startDecorator={<WhatsAppIcon sx={{ fontSize: '1.125rem' }} />}
                          sx={{ justifyContent: 'center', alignSelf: 'flex-start' }}
                        >
                          {t('invoiceForm.sendWhatsApp')}
                        </Button>
                      </Stack>
                    </PermissionGate>
                  )}
                </Stack>
              </Grid>
            </Grid>

          </FormControl>
        </form>

        {formID != null && (
          <InvoicePaymentLinksSection
            invoice={invoice}
            onInvoiceRefresh={refreshInvoiceQuietly}
          />
        )}

        {formID != null && (
          <Box sx={{ mt: 4 }}>
            <Typography level="h4" sx={{ mb: 2 }}>{t('invoiceForm.sectionPayments')}</Typography>
            <DataGrid
              hideFooter
              rows={(invoice && invoice.payments) || []}
              columns={getShipmentFormPaymentColumns(t)}
              loading={isLoading}
              sx={{ minHeight: 200 }}
            />
          </Box>
        )}
      </Box>

      <Modal open={openRegenerateInvoicesAlert} onClose={() => setOpenRegenerateInvoicesAlert(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            {t('invoiceForm.regenerateModalTitle')}
          </DialogTitle>
          <Divider />
          <DialogContent>
            {t('invoiceForm.regenerateModalBody')}
          </DialogContent>
          <DialogContent>
            <NativeCheckbox
              defaultChecked={sendInvoicesMessages}
              checked={sendInvoicesMessages}
              onChange={(event) => setSendInvoicesMessages(event.target.checked)}
              label={t('invoiceForm.sendInvoicesCheckbox')}
            />
          </DialogContent>

          <DialogActions>
            <PermissionGate permission={PERMISSIONS.INVOICES_REGENERATE}>
              <Button
                loading={isRegeneratingInvoicesLoading}
                variant="solid"
                color="success"
                onClick={regenerateInvoices}
              >
                {t('actions.yes')}
              </Button>
            </PermissionGate>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setOpenRegenerateInvoicesAlert(false)}
            >
              {t('actions.no')}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        autoHideDuration={3000}
        open={successUpdate}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSuccessUpdate(false);
        }}
      >
        {t('invoiceForm.snackbarUpdated')}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={failedUpdate}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setFailedUpdate(false);
        }}
      >
        {t('invoiceForm.snackbarFailedPrefix')} {String(errorMessage)}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={successGenerateInvoices}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSuccessGenerateInvoices(false);
        }}
      >
        {t('invoiceForm.snackbarRegenerated')}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={failedGenerateInvoices}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setFailedGenerateInvoices(false);
        }}
      >
        {invoicesResponseMessages}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={successSentMsg}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSuccessSentMsg(false);
        }}
      >
        {t('invoiceForm.snackbarWhatsAppOk')}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={failedSentMsg}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setFailedSentMsg(false);
        }}
      >
        {t('invoiceForm.snackbarWhatsAppFail')}
      </Snackbar>
    </Box>
  )
}


